import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { PersonsProvider, TagsProvider } from '@modeso/aumico-lib-companies-fe';
import { GetUserDto, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { combineLatest, filter } from 'rxjs';
import { PersonOption, TasksOverviewActions } from '../../../../../../aumico-lib-cpmm-fe/src/lib';
import { SortDirection } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/sort-direction.enum';
import { TaskState } from '../../../../../../aumico-lib-cpmm-fe/src/lib/enums/task-state.enum';
import {
    PersonProjectDto,
    PersonProjectsActions,
    PersonProjectSortBy,
    PersonProjectsFilter,
    PersonProjectTableValue,
    selectPersonProjects,
} from '../../../../../../aumico-lib-projects-fe/src/lib';
import { activeNavigation } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/activeNavigation.enum';
import { ProjectStatus } from '../../../../../../aumico-lib-projects-fe/src/lib/enums/project-status.enum';
import { AssigneeFilter } from '../../../enums/assigneeFilter.enum';
import { Paths } from '../../../enums/paths.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { lastProjectYearByPersonId, toPersonProjectTableData } from '../helpers/person-projects-table.helper';

export const PROJECTS_SUBJECT_TOGGLE = { company: 'COMPANY', person: 'PERSON' } as const;

/**
 * REV-22155 — projects page "Personen": like the company projects page (tabs, search, filters,
 * "+ Neues Projekt", bulk actions), for person projects (mandate management only).
 * Filters live on the page only; remembering them across navigation is the later filter rework.
 */
@UntilDestroy()
@Component({
    selector: 'app-page-person-projects-overview',
    templateUrl: 'person-projects-overview.page.html',
    styleUrls: ['../projects-overview/projects-overview.page.scss', 'person-projects-overview.page.scss'],
})
export class PersonProjectsOverviewPage extends BasePageComponent implements OnInit {
    readonly subjectToggleOptions = [
        { value: PROJECTS_SUBJECT_TOGGLE.company, label: this.i18next.t('aumico_projects_toggle_companies') as string },
        { value: PROJECTS_SUBJECT_TOGGLE.person, label: this.i18next.t('aumico_projects_toggle_persons') as string },
    ];
    readonly toggleValue = PROJECTS_SUBJECT_TOGGLE.person;

    // Starts on "Alle Projekte", like the company projects.
    displayAllProjects = true;
    activeNav = activeNavigation.ALL_PROJECTS;
    filter: PersonProjectsFilter = {};

    tableData: PersonProjectTableValue[] = [];
    showTags = false;

    personOptions: { id: string; name: string }[] = [];
    activePersons: PersonOption[] = [];
    assigneeOptions: { id: string; name: string }[] = [];
    tagOptions: { id: string; name: string }[] = [];
    lastProjectYearByPersonId: Record<string, number> = {};

    readonly statusOptions = [
        { label: this.i18next.t('aumico_projects_status_active') as string, value: ProjectStatus.ACTIVE },
        { label: this.i18next.t('aumico_projects_status_done') as string, value: ProjectStatus.DONE },
        { label: this.i18next.t('aumico_projects_status_archived') as string, value: ProjectStatus.ARCHIVED },
    ];

    showCreateProjectPopup = false;

    allProjectsSelected = false;
    selectedProjects = new Set<string>();
    readonly bulkActions = [{ id: 'STATUS_CHANGE', translationKey: 'aumico_project_status_change_action_bar' }];
    readonly deleteAction = { id: 'DELETE_PROJECTS', translationKey: 'aumico_project_delete_action_bar' };
    openBulkEditStatus = false;
    openBulkDeleteConfirmationDialog = false;
    openAutoTaskUpdateDialog = false;
    readonly possibleStatus = [
        { id: ProjectStatus.ACTIVE, name: this.i18next.t('aumico_projects_status_active') as string },
        { id: ProjectStatus.DONE, name: this.i18next.t('aumico_projects_status_done') as string },
    ];
    readonly bulkEditStatusControl = this.fb.control<{ id: ProjectStatus; name: string } | null>(null);

    private projects: PersonProjectDto[] = [];

    constructor(
        injector: Injector,
        private readonly i18next: I18NextService,
        private readonly store: Store,
        private readonly personsProvider: PersonsProvider,
        private readonly tagsProvider: TagsProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly fb: FormBuilder,
    ) {
        super(injector);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.organizationProvider.dispatchGetUsers(true);
        this.personsProvider.dispatchPersonsList();
        this.tagsProvider.dispatchTagsList();

        this.activeUserAumicoInternalId$
            .pipe(
                filter((id): id is string => !!id),
                untilDestroyed(this),
            )
            .subscribe(() => this.reload());

        combineLatest([
            this.store.select(selectPersonProjects),
            this.organizationProvider.getUsers(),
            this.personsProvider.getPersons$(),
            this.tagsProvider.getTagNamesById$(),
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([projects, users, persons, tagNamesById]) => {
                this.projects = projects;
                this.buildOptions(users, persons, tagNamesById);
                this.tableData = toPersonProjectTableData(
                    projects,
                    users,
                    persons,
                    tagNamesById,
                    this.i18next.t('aumico_tasks_unassigned') as string,
                );
                this.lastProjectYearByPersonId = lastProjectYearByPersonId(projects);
                this.dropStaleSelection();
            });

        // A new project opens its mandate management page.
        this.actionsListener$
            .pipe(ofType(PersonProjectsActions.personProjectCreated), untilDestroyed(this))
            .subscribe(({ project }) => {
                this.showCreateProjectPopup = false;
                this.openMandateManagement(project.projectId);
            });

        // Ticking a task in the progress flyout changes the counts: reload.
        this.actionsListener$
            .pipe(ofType(TasksOverviewActions.taskStatusForProjectByTaskIdUpdated), untilDestroyed(this))
            .subscribe(() => this.reload());
    }

    onSubjectToggle(value: string): void {
        if (value === PROJECTS_SUBJECT_TOGGLE.company) {
            void this.router.navigate([this.projectRoot, Paths.OVERVIEW]);
        }
    }

    onDisplayMyProjects(): void {
        this.setTab(false);
    }

    onDisplayAllProjects(): void {
        this.setTab(true);
    }

    onSearchFilterChange(term: string): void {
        this.updateFilter({ personName: term || undefined });
    }

    onPersonFilterChange(personIds: string[]): void {
        this.updateFilter({ personIds: personIds.length ? personIds : undefined });
    }

    onYearFilterChange(years: number[]): void {
        this.updateFilter({ years: years.length ? years : undefined });
    }

    onAssigneeFilterChange(userIds: string[]): void {
        this.updateFilter({ assignee: userIds.length ? userIds : undefined });
    }

    onStatusFilterChange(status: ProjectStatus[]): void {
        this.updateFilter({ projectStatus: status.length ? status : undefined });
    }

    onTagFilterChange(tagIds: string[]): void {
        this.updateFilter({ tagIds: tagIds.length ? tagIds : undefined });
    }

    sortProjects({ sortBy, sortOrder }: { sortBy: PersonProjectSortBy | null; sortOrder: SortDirection | null }): void {
        this.updateFilter({
            sortBy: sortBy ?? undefined,
            sortOrder: sortOrder === SortDirection.DESC ? 'DESC' : sortOrder === SortDirection.ASC ? 'ASC' : undefined,
        });
    }

    get activeSortOrder(): SortDirection | null {
        return this.filter.sortOrder === 'DESC' ? SortDirection.DESC : this.filter.sortOrder === 'ASC' ? SortDirection.ASC : null;
    }

    isAnyFilterActive(): boolean {
        const { personIds, personName, years, projectStatus, tagIds, assignee } = this.filter;
        return !!(personIds?.length || personName || years?.length || projectStatus?.length || tagIds?.length || assignee?.length);
    }

    openCreateProjectDialog(): void {
        this.showCreateProjectPopup = true;
    }

    onCreateProject({ personId, year }: { personId: string; year: number }): void {
        this.store.dispatch(PersonProjectsActions.createPersonProject({ personId, year }));
    }

    onSelectProject({ project, section }: { project: PersonProjectTableValue; section?: string }): void {
        this.openMandateManagement(project.projectId, section);
    }

    onUpdateProjectStatus({ projectId, status, updateTasks }: { projectId: string; status: ProjectStatus; updateTasks: boolean }): void {
        this.store.dispatch(
            PersonProjectsActions.updatePersonProjectStatus({
                projectId,
                status,
                updateTasks,
                mandateManagementTaskIds: updateTasks ? this.openTaskIds([projectId]) : [],
            }),
        );
    }

    onDeleteProject(projectId: string): void {
        this.store.dispatch(PersonProjectsActions.deletePersonProject({ projectId }));
    }

    onChangeTaskState({ projectId, taskId, taskState }: { projectId: string; taskId: string; taskState: TaskState }): void {
        this.store.dispatch(
            TasksOverviewActions.updateTaskStatusForProjectByTaskId({ projectId, taskId, updateTaskDto: { taskState } }),
        );
    }

    onToggleCheckAllProject(): void {
        this.allProjectsSelected = !this.allProjectsSelected;
        this.selectedProjects = this.allProjectsSelected ? new Set(this.tableData.map((p) => p.projectId)) : new Set();
    }

    onToggleCheckProject(projectId: string): void {
        const next = new Set(this.selectedProjects);
        if (next.has(projectId)) {
            next.delete(projectId);
        } else {
            next.add(projectId);
        }
        this.selectedProjects = next;
        this.allProjectsSelected = next.size > 0 && next.size === this.tableData.length;
    }

    onActionClicked(actionId: string): void {
        if (actionId === 'STATUS_CHANGE') {
            this.bulkEditStatusControl.setValue(null);
            this.openBulkEditStatus = true;
        } else if (actionId === 'DELETE_PROJECTS') {
            this.openBulkDeleteConfirmationDialog = true;
        }
    }

    editStatusSelected(): void {
        const status = this.bulkEditStatusControl.value?.id;
        if (status == null) {
            return;
        }
        this.openBulkEditStatus = false;
        if (status === ProjectStatus.DONE) {
            this.openAutoTaskUpdateDialog = true;
        } else {
            this.bulkUpdateStatus(status, false);
        }
    }

    onEditStatusConfirm(updateTasks: boolean): void {
        this.openAutoTaskUpdateDialog = false;
        this.bulkUpdateStatus(ProjectStatus.DONE, updateTasks);
    }

    confirmDeleteBulkProjects(): void {
        this.openBulkDeleteConfirmationDialog = false;
        this.store.dispatch(PersonProjectsActions.bulkDeletePersonProjects({ projectIds: Array.from(this.selectedProjects) }));
        this.clearSelection();
    }

    private bulkUpdateStatus(status: ProjectStatus, updateTasks: boolean): void {
        const projectIds = Array.from(this.selectedProjects);
        this.store.dispatch(
            PersonProjectsActions.bulkUpdatePersonProjectStatus({
                projectIds,
                status,
                updateTasks,
                mandateManagementTaskIds: updateTasks ? this.openTaskIds(projectIds) : [],
            }),
        );
        this.clearSelection();
    }

    /** Open tasks of the projects, from their progress data — closed when "erledigt" asks to. */
    private openTaskIds(projectIds: string[]): string[] {
        return this.projects
            .filter((p) => projectIds.includes(p.projectId))
            .flatMap((p) => p.workflowData ?? [])
            .flatMap((w) => w.issuesDetails)
            .filter((issue) => issue.state !== TaskState.DONE && issue.state !== TaskState.NOT_APPLICABLE)
            .map((issue) => issue.issueId);
    }

    private setTab(all: boolean): void {
        if (this.displayAllProjects === all) {
            return;
        }
        this.displayAllProjects = all;
        this.activeNav = all ? activeNavigation.ALL_PROJECTS : activeNavigation.MY_PROJECTS;
        this.filter = { ...this.filter, assignee: undefined };
        this.reload();
    }

    private updateFilter(changes: Partial<PersonProjectsFilter>): void {
        this.filter = { ...this.filter, ...changes };
        this.reload();
    }

    /** "Meine Projekte": the user is mandate manager or clerk — like companies. */
    private reload(): void {
        const filter: PersonProjectsFilter = this.displayAllProjects
            ? this.filter
            : { ...this.filter, assignee: [this.activeUserAumicoInternalId] };
        this.store.dispatch(PersonProjectsActions.loadPersonProjects({ filter }));
    }

    private openMandateManagement(projectId: string, section?: string): void {
        void this.router.navigate(
            [this.projectRoot, Paths.PERSON_PROJECTS, projectId, Paths.MANDATE_MANAGEMENT],
            section != null ? { queryParams: { section } } : {},
        );
    }

    /** Routes start with the language: `/de-CH/home/project`. */
    private get projectRoot(): string {
        return `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.PROJECT}`;
    }

    private buildOptions(users: GetUserDto[], persons: IPersonDTO[], tagNamesById: Record<string, string>): void {
        this.personOptions = persons.map((p) => ({ id: p._id!, name: `${p.lastName} ${p.firstName}` }));
        this.activePersons = persons.filter((p) => p.active !== false).map((p) => ({ _id: p._id!, name: `${p.lastName} ${p.firstName}` }));
        this.tagOptions = Object.entries(tagNamesById).map(([id, name]) => ({ id, name }));
        this.showTags = this.tagOptions.length > 0;

        const active = users.filter((u) => !u.isDeleted && !u.isInvitee);
        const me = active.filter((u) => u.isMe);
        this.assigneeOptions = [
            ...me.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` })),
            { id: AssigneeFilter.unassigned, name: this.i18next.t('aumico_tasks_unassigned') as string },
            ...active.filter((u) => !u.isMe).map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` })),
        ];
    }

    private dropStaleSelection(): void {
        const ids = new Set(this.tableData.map((p) => p.projectId));
        const kept = new Set(Array.from(this.selectedProjects).filter((id) => ids.has(id)));
        if (kept.size !== this.selectedProjects.size) {
            this.selectedProjects = kept;
            this.allProjectsSelected = kept.size > 0 && kept.size === ids.size;
        }
    }

    private clearSelection(): void {
        this.selectedProjects = new Set();
        this.allProjectsSelected = false;
    }
}
